import React, { FC, useEffect, useState } from 'react'

import { PopupControls, usePopupControls } from 'shared/hooks/usePopupControls'
import { addMonths, addYears, subMonths, subYears } from 'shared/utils/date/getPeriod'

import { DropdownHorizontalPosition, DropdownVerticalPosition } from 'components/UIKit/Dropdown'

import { useDatePickerInterval } from '../hooks/useDatePickerInterval'
import { CalendarValue, DatePickerOnChangeFunction, DatePickerVariant, IntervalVariants } from '../types'
import { DatePickerContext } from './DatePickerContext'

type Props = {
  value: CalendarValue
  variant: DatePickerVariant
  onChange: DatePickerOnChangeFunction
  horizontalPosition?: DropdownHorizontalPosition
  verticalPosition?: DropdownVerticalPosition
  modalControls: PopupControls
  disabledDates?: Date[]
  maxDate?: Date
  minDate?: Date
  intervals?: IntervalVariants[]
  interval?: IntervalVariants
  isInputAvailable?: boolean
  setInterval?: (interval: IntervalVariants) => void
  onError?: (error: string) => void
}

const DatePickerProvider: FC<Props> = ({
  children,
  value,
  variant,
  onChange = () => {},
  verticalPosition,
  horizontalPosition,
  disabledDates,
  minDate,
  maxDate,
  modalControls,
  intervals,
  setInterval,
  interval,
  isInputAvailable,
  onError,
}) => {
  const [controlDate, setControlDate] = useState<Date>(() => {
    if (!value) {
      return maxDate || new Date()
    }

    if (Array.isArray(value)) {
      return value[0] || new Date()
    }

    return value || new Date()
  })

  const { currentInterval, handleChangeInterval } = useDatePickerInterval({
    intervals,
    interval,
    onChangeInterval: setInterval,
  })
  const [startRange, setStartRange] = useState<Date | null>(() => {
    if (Array.isArray(value)) {
      return value[0]
    }

    return value
  })
  const [endRange, setEndRange] = useState<Date | null>(() => {
    if (Array.isArray(value)) {
      return value[1]
    }

    return value
  })

  const changeInterval = (newInterval: string) => {
    if (newInterval !== currentInterval) {
      handleChangeInterval(newInterval as IntervalVariants)
    }
  }

  const prevControlDate = (e: React.SyntheticEvent) => {
    e.preventDefault()
    e.stopPropagation()

    setControlDate(prev => {
      if (currentInterval === IntervalVariants.DAY) {
        return subMonths(prev, 1)
      }
      return subYears(prev, 1)
    })
  }

  const nextControlDate = (e: React.SyntheticEvent) => {
    e.preventDefault()
    e.stopPropagation()

    setControlDate(prev => {
      if (currentInterval === IntervalVariants.DAY) {
        return addMonths(prev, 1)
      }
      return addYears(prev, 1)
    })
  }

  const selectDate = (date: Date | null) => {
    setStartRange(date)
    setEndRange(date)
    onChange(date)
  }

  const reset = () => {
    if (Array.isArray(value)) {
      setStartRange(value[0])
      setEndRange(value[1])
      setControlDate(value[0] || new Date())
    } else {
      setStartRange(value)
      setEndRange(value)
      setControlDate(value || maxDate || new Date())
    }
  }

  const selectStartRange = (date: Date | null) => {
    setStartRange(date)
    if (date) {
      setControlDate(date)
    }
  }

  useEffect(() => {
    reset()
  }, [value])

  const closePopup = () => {
    modalControls.closePopup()

    changeInterval(IntervalVariants.DAY)

    if (Array.isArray(value)) {
      setControlDate(value[0] || new Date())
    } else {
      setControlDate(value || maxDate || new Date())
    }
  }

  return (
    <DatePickerContext.Provider
      value={{
        variant,
        interval: currentInterval,
        changeInterval,
        controlDate,
        prevControlDate,
        nextControlDate,
        setControlDate,
        isInputAvailable,
        selectEndRange: setEndRange,
        selectStartRange,
        endRange,
        selectDate,
        startRange,
        value,
        modalControls: {
          ...modalControls,
          closePopup,
        },
        onChange,
        reset,
        verticalPosition,
        horizontalPosition,
        disabledDates,
        minDate,
        maxDate,
        intervals,
        onError,
      }}>
      {children}
    </DatePickerContext.Provider>
  )
}

export { DatePickerProvider }
